import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { FilteredActivityFeedTypes } from '@components/Feed/store';
import { useActivityFeed } from '@components/Feed/useActivityFeed';
import { CollectorCard } from '@components/SongCards/CollectorCard';
import { PurchaseCardSkeleton } from '@components/SongCards/PurchaseCard/PurchaseCardSkeleton';
import { EmptyState } from '@components/basics/EmptyState';
import { Button } from '@components/main-components/button/Button';
import { GridCarousel } from '@components/main-components/carousel/GridCarousel';
import { useAuth } from '@context/auth/base';
import {
  ActivityFeedGroupFilterOption,
  PeopleFollowMintedDocument,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import {
  CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  GRID_CAROUSEL_INITIAL_PAGE,
  NAV_LINKS,
} from '@utils/constants';
import { getNextTrackIds } from '@utils/helpers';
import { LandingHeading } from '../shared/LandingHeading';

/*#__PURE__*/ gql(/* GraphQL */ `
  query PeopleFollowMinted($pagination: CursorConnectionArgs, $type: ActivityFeedType!) {
    activityFeed(filter: { activityFeedType: $type }) {
      id
      groups(filter: { types: COLLECTED }, pagination: $pagination) {
        edges {
          node {
            id
            information {
              __typename
              ... on SongCollectedByManyAggregate {
                collectedRelease {
                  release {
                    id
                    track {
                      id
                    }
                    ...CollectorCard
                  }
                }
                featuredCollectors {
                  nftsCount
                  user {
                    id
                    ...CollectorCardUserInfo
                  }
                }
              }

              ... on UserCollectedManySongsAggregate {
                user {
                  id
                  ...CollectorCardUserInfo
                }
                collectedReleases {
                  totalOwnedEditions
                  release {
                    id
                    track {
                      id
                    }
                    ...CollectorCard
                  }
                }
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

export const PeopleFollowMintedFeed = ({ hideTitle }: { hideTitle?: boolean }) => {
  const { authUser, isConnectingAPIUser } = useAuth();
  const authUserId = authUser?.id;

  const { orderedList, isInitialLoading, loadMoreNextPage } = useInfiniteQuery(
    PeopleFollowMintedDocument,
    {
      filterQueryKey: {
        authUserId,
      },
      variables({ pageParam }) {
        return {
          pagination: {
            first: GRID_CAROUSEL_INITIAL_PAGE,
            after: pageParam?.after || null,
          },
          type: FilteredActivityFeedTypes.User,
        };
      },
      enabled: !isConnectingAPIUser,
      getNextPageParam({ data }) {
        return (
          data.activityFeed?.groups.pageInfo.hasNextPage && {
            after: data.activityFeed.groups.pageInfo.endCursor,
          }
        );
      },
      list({ activityFeed }) {
        const activities = activityFeed?.groups?.edges.map(node => node?.node);
        // returning a new object that will give us a collector with a release
        // flatten the activities into an array of this object type so that we can get
        // a list of collectors with their recent mints
        const releases = activities
          ?.flatMap(activity => {
            if (!activity?.information) return null;

            if (activity.information.__typename === 'SongCollectedByManyAggregate') {
              const data = activity.information;

              return activity.information.featuredCollectors.map(collector => {
                return {
                  id: data.collectedRelease.release.id + collector.user.id + activity.id,
                  release: data.collectedRelease,
                  collector: collector.user,
                  nftsCount: collector.nftsCount,
                };
              });
            }
            if (activity.information.__typename === 'UserCollectedManySongsAggregate') {
              const data = activity.information;

              return data.collectedReleases.map(release => {
                return {
                  id: release.release.id + data.user.id + activity.id,
                  release,
                  collector: data.user,
                  nftsCount: release.totalOwnedEditions,
                };
              });
            }
            return null;
          })
          .filter(Boolean);

        return releases;
      },
      uniq(data) {
        return data.id;
      },
      keepPreviousData: true,
      ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
    },
  );

  const skeletonSlides = Array.from({ length: 5 }).map((_, i) => ({
    id: i.toString(),
    slide: <PurchaseCardSkeleton key={`freshly-minted-skeleton-${i}`} />,
  }));

  const releaseSlides = React.useMemo(
    () =>
      orderedList.map(collectedRelease => {
        return {
          id: collectedRelease.id,
          slide: (
            <CollectorCard
              key={collectedRelease.id}
              releaseFrag={collectedRelease.release.release}
              collectorFrag={collectedRelease.collector}
              editionsMinted={collectedRelease.nftsCount}
              getNextTrackIds={getNextTrackIds({
                trackId: collectedRelease.release.release.track.id,
                releases: orderedList.map(item => item.release.release),
              })}
            />
          ),
        };
      }),
    [orderedList],
  );

  const { setActivityFeedGroupFilter, setActivityFeedFilter } = useActivityFeed({ disabled: true });

  const setFollowFilter = useStableCallback(() => {
    setActivityFeedGroupFilter([ActivityFeedGroupFilterOption.Collected]);
    setActivityFeedFilter('USER');
  });

  const setNotableCollectorsFilter = () => {
    setActivityFeedGroupFilter([ActivityFeedGroupFilterOption.Collected]);
    setActivityFeedFilter(FilteredActivityFeedTypes.NotableCollectors);
  };

  if (isInitialLoading)
    return (
      <GridCarousel
        slides={skeletonSlides}
        onEndReached={null}
        onInternalActiveIndexChange={null}
        columnAmount={null}
        hideArrows={false}
        externalActiveIndex={null}
        spacing={null}
        alignSlidesLeft={false}
        ssrSlideWidth={undefined}
        columnType="default"
      />
    );

  const hasEmptySlides = !releaseSlides || releaseSlides.length === 0;

  if (hasEmptySlides) {
    return (
      <EmptyState
        title="It's quiet in here"
        subTitle="Your followed collectors don’t have any activity"
        button={
          <Button
            variant="primary"
            label="View all activity"
            href={NAV_LINKS.Feed}
            onClick={setNotableCollectorsFilter}
          />
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-y-4">
      {!hideTitle && (
        <LandingHeading
          header="People I Follow Minted"
          pathnameUri={NAV_LINKS.Feed}
          onHeaderClick={setFollowFilter}
        />
      )}
      <GridCarousel
        slides={releaseSlides}
        onEndReached={loadMoreNextPage}
        onInternalActiveIndexChange={null}
        columnAmount={null}
        hideArrows={false}
        externalActiveIndex={null}
        spacing={null}
        alignSlidesLeft={false}
        ssrSlideWidth={undefined}
        columnType="default"
      />
    </div>
  );
};
